/**
 * Global Error Handler - Centralized error management
 * Handles all API errors and provides safe user messages
 */

const ERROR_MESSAGES = {
    400: "Invalid request. Please check your input.",
    401: "Your session has expired. Please log in again.",
    403: "You don't have permission for this action.",
    404: "Resource not found.",
    408: "Request timeout. Please try again.",
    429: "Too many requests. Please wait a moment.",
    500: "Server error. Please try again later.",
    502: "Service temporarily unavailable.",
    503: "Service is under maintenance.",
    NETWORK_ERROR: "Network connection failed. Please check your internet.",
    TIMEOUT_ERROR: "Request took too long. Please try again.",
    UNKNOWN_ERROR: "Something went wrong. Please try again."
  };
  
  class GlobalErrorHandler {
    constructor() {
      this.isDevelopment = process.env.NODE_ENV === 'development';
    }
  
    /**
     * Extract user-friendly message from error object
     * Prevents exposure of sensitive information
     */
    getUserMessage(error) {
      // Check for network errors
      if (!error.response && error.request) {
        return ERROR_MESSAGES.NETWORK_ERROR;
      }
  
      // Check for timeout errors
      if (error.code === 'ECONNABORTED') {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
  
      // Check for HTTP status errors
      const status = error.response?.status;
      if (ERROR_MESSAGES[status]) {
        return ERROR_MESSAGES[status];
      }
  
      // Fallback to generic message
      return ERROR_MESSAGES.UNKNOWN_ERROR;
    }
  
    /**
     * Safe error logging - details only in development
     */
    logError(error, context = 'Unknown') {
      if (this.isDevelopment) {
        console.error(`[${context}]`, {
          status: error.response?.status,
          message: error.message,
          url: error.config?.url,
        });
      } else {
        // Production: minimal logging
        console.error(`[${context}] Error ${error.response?.status || 'UNKNOWN'}`);
      }
    }
  
    /**
     * Handle error and return safe message
     */
    handleError(error, context = 'API Error') {
      const userMessage = this.getUserMessage(error);
      this.logError(error, context);
      return userMessage;
    }
  
    /**
     * Handle 401 Unauthorized
     */
    handleUnauthorized() {
      this.logError({ response: { status: 401 } }, 'Unauthorized');
      sessionStorage.clear();
      window.location.href = "/";
    }
  
    /**
     * Check if error is CSRF related
     */
    handleForbidden(error) {
      const errorMessage = error.response?.data?.message || "";
      if (
        errorMessage.toLowerCase().includes("csrf") ||
        errorMessage.toLowerCase().includes("token")
      ) {
        return true;
      }
      return false;
    }
  }
  
  // Export singleton instance
  export const globalErrorHandler = new GlobalErrorHandler();